import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { CartProvider } from '@/lib/cart-context';
import { CurrencyProvider } from '@/lib/currency-context';
import { WishlistProvider } from '@/lib/wishlist-context';
import { Layout } from '@/components/layout';
import ImpersonateBanner from '@/components/ImpersonateBanner';
import CookieConsent from '@/components/CookieConsent';
import ScrollToTop from '@/components/ScrollToTop';
import ErrorBoundary from '@/components/ErrorBoundary';
import { languages } from '@/lib/i18n';
import {
  HomePage,
  ProductsPage,
  ProductDetailPage,
  CartPage,
  CheckoutPage,
  LoginPage,
  OrdersPage,
  DashboardPage,
  AccountSettingsPage,
  SellerDashboardPage,
  AdminDashboardPage,
  SellerBulkUploadPage,
  CarRentalsPage,
  HotelFlightsPage,
  SearchPage,
  VerifyEmailPage,
  RegistrationSuccessPage,
  RestaurantsPage,
} from '@/pages';
import AiModePage from '@/pages/AiModePage';
import XmlMappingPage from '@/pages/seller/XmlMappingPage'; // kept for potential direct access
import RestaurantDetailPage from '@/pages/RestaurantDetailPage';
import RestaurantEmailCampaign from '@/pages/admin/RestaurantEmailCampaign';
import SellerUploadsPage from '@/pages/admin/SellerUploadsPage';
import SupermarketsPage from '@/pages/SupermarketsPage';
import SupermarketStorePage from '@/pages/SupermarketStorePage';
import SupermarketOrderPage from '@/pages/SupermarketOrderPage';
import MembershipPage from '@/pages/MembershipPage';
import SavedRestaurantsPage from '@/pages/SavedRestaurantsPage';
import SellerOnboardingPage from '@/pages/SellerOnboardingPage';
import SellerRegisterPage from '@/pages/SellerRegisterPage';
import SellerGuidePage from '@/pages/SellerGuidePage';
import SellerRegistrationPendingPage from '@/pages/SellerRegistrationPendingPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import FinanceDashboardPage from '@/pages/FinanceDashboardPage';
import CareersPage from '@/pages/CareersPage';
import NewsletterUnsubscribePage from '@/pages/NewsletterUnsubscribePage';
import ContactPage from '@/pages/ContactPage';
import HomepageManagementPage from '@/pages/HomepageManagementPage';
import StaticPage from '@/pages/StaticPage';
import TireAppointmentPage from '@/pages/TireAppointmentPage';
import MyAppointmentsPage from '@/pages/MyAppointmentsPage';
import ShippingPage from '@/pages/ShippingPage';
import ReturnsPage from '@/pages/ReturnsPage';
import OrderTrackingPage from '@/pages/OrderTrackingPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/TermsOfServicePage';
import CookiePolicyPage from '@/pages/CookiePolicyPage';
import ZiraatCallbackPage from '@/pages/ZiraatCallbackPage';
import TaxiLimousinePage from '@/pages/TaxiLimousinePage';
import TaxiBookingDetail from '@/components/taxi/TaxiBookingDetail';
import FlightSearchPage from '@/pages/FlightSearchPage';
import WishlistPage from '@/pages/WishlistPage';
import FlightBookingPage from '@/pages/FlightBookingPage';
import FlightBookingSuccessPage from '@/pages/FlightBookingSuccessPage';
import HotelSearchPage from '@/pages/HotelSearchPage';
import HotelDetailPage from '@/pages/HotelDetailPage';
import HotelBookingPage from '@/pages/HotelBookingPage';
import HotelBookingSuccessPage from '@/pages/HotelBookingSuccessPage';
import CarRentalBookingPage from '@/pages/CarRentalBookingPage';
import CarRentalBookingSuccessPage from '@/pages/CarRentalBookingSuccessPage';
import BijuteriPage from '@/pages/BijuteriPage';
import SellerStorePage from '@/pages/SellerStorePage';
import SupportPage from '@/pages/SupportPage';
import { getLocaleFromPathname, buildPathForLocale, CAR_RENTAL_LOCALIZED_SLUG } from '@/lib/intl-seo';
import { RouteSeo } from '@/components/seo/RouteSeo';
import '@/App.css';

// Supported language codes for URL routing
const supportedLangCodes = languages.map(l => l.code);

/** /carriers vb. yanlış veya İngilizce footer bağlantısı → iş / kariyer başvuru sayfası */
function RedirectCarriersToCareersLang() {
  const { lang } = useParams();
  if (!lang || !supportedLangCodes.includes(lang)) {
    return <Navigate to="/careers" replace />;
  }
  return <Navigate to={buildPathForLocale(lang, ['careers'])} replace />;
}

/** /aktar → baharat kategorisi /category/aktar (araç kiralama değil) */
function RedirectAktarToCategory() {
  const { lang } = useParams();
  const location = useLocation();
  const to = lang && supportedLangCodes.includes(lang)
    ? buildPathForLocale(lang, ['category', 'aktar'])
    : '/category/aktar';
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

/** /jewelry ve /taki → kanonik /bijuteri (dil önekli veya İngilizce kök) */
function RedirectToEnglishSegments({ segments }) {
  const { lang } = useParams();
  const location = useLocation();
  const locale = lang && supportedLangCodes.includes(lang) ? lang : getLocaleFromPathname(location.pathname);
  const to = buildPathForLocale(locale, segments);
  return <Navigate to={`${to}${location.search}${location.hash}`} replace />;
}

/** /en ve /en/* → kök İngilizce URL’lere (canonical) */
const RedirectStripEnPrefix = () => {
  const location = useLocation();
  const to = React.useMemo(() => {
    let p = location.pathname;
    if (p.startsWith('/en')) {
      p = p.slice(3) || '/';
    }
    return `${p}${location.search}${location.hash}`;
  }, [location.pathname, location.search, location.hash]);
  return <Navigate to={to} replace />;
};

// Language wrapper component for SEO-friendly URLs
const LanguageWrapper = ({ children }) => {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const location = useLocation();
  const effectiveLang = lang || getLocaleFromPathname(location.pathname);

  React.useEffect(() => {
    if (effectiveLang && supportedLangCodes.includes(effectiveLang)) {
      i18n.changeLanguage(effectiveLang);
      document.documentElement.lang = effectiveLang;
    }
  }, [effectiveLang]);

  return children;
};

// Protected route wrapper
const ProtectedRoute = ({ children, requireRole }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (requireRole && user.role !== requireRole && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public route - redirect to dashboard if already logged in
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Don't redirect if checking for OAuth callback
  const hash = window.location.hash;
  if (hash && hash.includes('session_id=')) {
    return children;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Kök = İngilizce (Layout pathname ile i18n eşitler) */}
      <Route path="/" element={<Layout><HomePage /></Layout>} />
      <Route path="/en" element={<RedirectStripEnPrefix />} />
      <Route path="/en/*" element={<RedirectStripEnPrefix />} />
      <Route path="/tr/flights" element={<Navigate to="/tr/ucuslar" replace />} />
      <Route path="/de/flights" element={<Navigate to="/de/fluege" replace />} />
      <Route path="/tr/ucuslar" element={<LanguageWrapper><Layout><FlightSearchPage /></Layout></LanguageWrapper>} />
      <Route path="/de/fluege" element={<LanguageWrapper><Layout><FlightSearchPage /></Layout></LanguageWrapper>} />
      <Route path="/tr/contact" element={<Navigate to="/tr/iletisim" replace />} />
      <Route path="/de/contact" element={<Navigate to="/de/kontakt" replace />} />
      <Route path="/tr/iletisim" element={<LanguageWrapper><Layout><ContactPage /></Layout></LanguageWrapper>} />
      <Route path="/de/kontakt" element={<LanguageWrapper><Layout><ContactPage /></Layout></LanguageWrapper>} />
      <Route path="/tr/careers" element={<Navigate to="/tr/kariyer" replace />} />
      <Route path="/tr/kariyer" element={<LanguageWrapper><Layout><CareersPage /></Layout></LanguageWrapper>} />

      {/* Footer "Carriers" → kariyer başvuru sayfası */}
      <Route path="/:lang/carriers" element={<RedirectCarriersToCareersLang />} />
      {/* Support — /:lang'dan önce (aksi halde lang=support ana sayfa açar) */}
      <Route path="/support" element={<Layout><SupportPage /></Layout>} />
      <Route path="/tr/destek" element={<LanguageWrapper><Layout><SupportPage /></Layout></LanguageWrapper>} />
      <Route path="/tr/support" element={<Navigate to="/tr/destek" replace />} />
      <Route path="/:lang/support" element={<LanguageWrapper><Layout><SupportPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/destek" element={<LanguageWrapper><Layout><SupportPage /></Layout></LanguageWrapper>} />
      {/* İngilizce kök: /bijuteri  (/:lang bunu ana sayfa sanmasın) */}
      <Route path="/bijuteri" element={<Layout><BijuteriPage /></Layout>} />
      <Route path="/jewelry" element={<RedirectToEnglishSegments segments={['bijuteri']} />} />
      <Route path="/taki" element={<RedirectToEnglishSegments segments={['bijuteri']} />} />
      <Route path="/:lang" element={<LanguageWrapper><Layout><HomePage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/category/:categorySlug" element={<LanguageWrapper><Layout><ProductsPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/products" element={<LanguageWrapper><Layout><ProductsPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/store/:storeSlug" element={<LanguageWrapper><Layout><SellerStorePage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/magaza/:storeSlug" element={<LanguageWrapper><Layout><SellerStorePage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/shop/:storeSlug" element={<LanguageWrapper><Layout><SellerStorePage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/product/:id" element={<LanguageWrapper><Layout><ProductDetailPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/cart" element={<LanguageWrapper><Layout><CartPage /></Layout></LanguageWrapper>} />
      {Object.entries(CAR_RENTAL_LOCALIZED_SLUG).map(([lang, slug]) => (
        <Route key={`${lang}-car-rental-alias`} path={`/${lang}/car-rentals`} element={<Navigate to={`/${lang}/${slug}`} replace />} />
      ))}
      {Object.entries(CAR_RENTAL_LOCALIZED_SLUG).map(([lang, slug]) => (
        <Route key={`${lang}-car-rental-book-alias`} path={`/${lang}/car-rentals/booking`} element={<Navigate to={`/${lang}/${slug}/booking`} replace />} />
      ))}
      {Object.entries(CAR_RENTAL_LOCALIZED_SLUG).map(([lang, slug]) => (
        <Route key={`${lang}-car-rental-ok-alias`} path={`/${lang}/car-rentals/booking/success`} element={<Navigate to={`/${lang}/${slug}/booking/success`} replace />} />
      ))}
      {Object.entries(CAR_RENTAL_LOCALIZED_SLUG).map(([lang, slug]) => (
        <Route key={`${lang}-car-rental`} path={`/${lang}/${slug}`} element={<LanguageWrapper><Layout><CarRentalsPage /></Layout></LanguageWrapper>} />
      ))}
      {Object.entries(CAR_RENTAL_LOCALIZED_SLUG).map(([lang, slug]) => (
        <Route key={`${lang}-car-rental-book`} path={`/${lang}/${slug}/booking`} element={<LanguageWrapper><Layout><CarRentalBookingPage /></Layout></LanguageWrapper>} />
      ))}
      {Object.entries(CAR_RENTAL_LOCALIZED_SLUG).map(([lang, slug]) => (
        <Route key={`${lang}-car-rental-ok`} path={`/${lang}/${slug}/booking/success`} element={<LanguageWrapper><Layout><CarRentalBookingSuccessPage /></Layout></LanguageWrapper>} />
      ))}
      <Route path="/:lang/car-rentals" element={<LanguageWrapper><Layout><CarRentalsPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/car-rentals/booking" element={<LanguageWrapper><Layout><CarRentalBookingPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/car-rentals/booking/success" element={<LanguageWrapper><Layout><CarRentalBookingSuccessPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/aktar" element={<RedirectAktarToCategory />} />
      <Route path="/:lang/taxi-limousine" element={<LanguageWrapper><Layout><TaxiLimousinePage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/taxi-limousine/booking/:bookingId" element={<LanguageWrapper><TaxiBookingDetail /></LanguageWrapper>} />
      <Route path="/:lang/hotel-flights" element={<LanguageWrapper><Layout><HotelFlightsPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/flights" element={<LanguageWrapper><Layout><FlightSearchPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/flights/booking" element={<LanguageWrapper><Layout><FlightBookingPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/flights/booking/success" element={<LanguageWrapper><Layout><FlightBookingSuccessPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/hotels" element={<LanguageWrapper><Layout><HotelSearchPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/hotels/:hotelId" element={<LanguageWrapper><Layout><HotelDetailPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/hotels/booking" element={<LanguageWrapper><Layout><HotelBookingPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/hotels/booking/success" element={<LanguageWrapper><Layout><HotelBookingSuccessPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/restaurants" element={<LanguageWrapper><Layout><RestaurantsPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/restaurants/:id" element={<LanguageWrapper><Layout><RestaurantDetailPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/supermarkets" element={<LanguageWrapper><Layout><SupermarketsPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/supermarkets/store/:sellerId" element={<LanguageWrapper><Layout><SupermarketStorePage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/supermarkets/order/:orderId" element={<LanguageWrapper><Layout><SupermarketOrderPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/membership" element={<LanguageWrapper><Layout><MembershipPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/membership/success" element={<LanguageWrapper><Layout><MembershipPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/seller-guide" element={<LanguageWrapper><Layout><SellerGuidePage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/search" element={<LanguageWrapper><Layout><SearchPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/ai" element={<LanguageWrapper><Layout><AiModePage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/contact" element={<LanguageWrapper><Layout><ContactPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/careers" element={<LanguageWrapper><Layout><CareersPage /></Layout></LanguageWrapper>} />

      {/* Bijuteri/Jewelry Category Page */}
      <Route path="/:lang/bijuteri" element={<LanguageWrapper><Layout><BijuteriPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/jewelry" element={<RedirectToEnglishSegments segments={['bijuteri']} />} />
      <Route path="/:lang/taki" element={<RedirectToEnglishSegments segments={['bijuteri']} />} />

      {/* Language-prefixed Tire Appointment Routes */}
      <Route path="/:lang/tire-appointment" element={<LanguageWrapper><Layout><TireAppointmentPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/my-appointments" element={<LanguageWrapper><Layout><MyAppointmentsPage /></Layout></LanguageWrapper>} />

      {/* Localized About & Refund Policy slugs */}
      <Route path="/tr/hakkimizda" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/tr/about" element={<Navigate to="/tr/hakkimizda" replace />} />
      <Route path="/tr/iade-politikasi" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/tr/refund-policy" element={<Navigate to="/tr/iade-politikasi" replace />} />
      <Route path="/de/uber-uns" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/de/about" element={<Navigate to="/de/uber-uns" replace />} />
      <Route path="/de/ruckerstattung" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/de/refund-policy" element={<Navigate to="/de/ruckerstattung" replace />} />
      <Route path="/fr/a-propos" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/fr/about" element={<Navigate to="/fr/a-propos" replace />} />
      <Route path="/fr/politique-de-remboursement" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/fr/refund-policy" element={<Navigate to="/fr/politique-de-remboursement" replace />} />
      <Route path="/es/sobre-nosotros" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/es/about" element={<Navigate to="/es/sobre-nosotros" replace />} />
      <Route path="/es/politica-de-reembolso" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/es/refund-policy" element={<Navigate to="/es/politica-de-reembolso" replace />} />
      <Route path="/it/chi-siamo" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/it/about" element={<Navigate to="/it/chi-siamo" replace />} />
      <Route path="/it/politica-di-rimborso" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/it/refund-policy" element={<Navigate to="/it/politica-di-rimborso" replace />} />
      <Route path="/pt/sobre-nos" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/pt/about" element={<Navigate to="/pt/sobre-nos" replace />} />
      <Route path="/pt/politica-de-reembolso" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/pt/refund-policy" element={<Navigate to="/pt/politica-de-reembolso" replace />} />
      <Route path="/ru/o-nas" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/ru/about" element={<Navigate to="/ru/o-nas" replace />} />
      <Route path="/ru/politika-vozvrata" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/ru/refund-policy" element={<Navigate to="/ru/politika-vozvrata" replace />} />
      <Route path="/zh/guanyu-women" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/zh/about" element={<Navigate to="/zh/guanyu-women" replace />} />
      <Route path="/zh/tuikuan-zhengce" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/zh/refund-policy" element={<Navigate to="/zh/tuikuan-zhengce" replace />} />
      <Route path="/fa/darbare-ma" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/fa/about" element={<Navigate to="/fa/darbare-ma" replace />} />
      <Route path="/fa/siasat-bazgasht" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/fa/refund-policy" element={<Navigate to="/fa/siasat-bazgasht" replace />} />

      {/* Language-prefixed Static Pages */}
      <Route path="/:lang/about" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/kvkk" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/privacy" element={<LanguageWrapper><Layout><PrivacyPolicyPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/privacy-policy" element={<LanguageWrapper><Layout><PrivacyPolicyPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/data-protection" element={<LanguageWrapper><Layout><PrivacyPolicyPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/datenschutz" element={<LanguageWrapper><Layout><PrivacyPolicyPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/terms" element={<LanguageWrapper><Layout><TermsOfServicePage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/terms-of-service" element={<LanguageWrapper><Layout><TermsOfServicePage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/nutzungsbedingungen" element={<LanguageWrapper><Layout><TermsOfServicePage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/cookies" element={<LanguageWrapper><Layout><CookiePolicyPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/cookie-policy" element={<LanguageWrapper><Layout><CookiePolicyPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/cerez-politikasi" element={<LanguageWrapper><Layout><CookiePolicyPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/refund-policy" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/shipping" element={<LanguageWrapper><Layout><ShippingPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/returns" element={<LanguageWrapper><Layout><ReturnsPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/track" element={<LanguageWrapper><Layout><OrderTrackingPage /></Layout></LanguageWrapper>} />
      <Route path="/:lang/page/:slug" element={<LanguageWrapper><Layout><StaticPage /></Layout></LanguageWrapper>} />

      {/* Legacy non-prefixed public routes (for backwards compatibility) */}
      <Route path="/products" element={<Layout><ProductsPage /></Layout>} />
      <Route path="/store/:storeSlug" element={<Layout><SellerStorePage /></Layout>} />
      <Route path="/magaza/:storeSlug" element={<Layout><SellerStorePage /></Layout>} />
      {/* en kök SEO: /category/:slug (buildPathForLocale('en', …)) — /en/* strip edildiği için önek yok */}
      <Route path="/category/:categorySlug" element={<Layout><ProductsPage /></Layout>} />
      <Route path="/product/:id" element={<Layout><ProductDetailPage /></Layout>} />
      <Route path="/cart" element={<Layout><CartPage /></Layout>} />
      <Route path="/car-rentals" element={<Layout><CarRentalsPage /></Layout>} />
      <Route path="/car-rentals/booking" element={<Layout><CarRentalBookingPage /></Layout>} />
      <Route path="/car-rentals/booking/success" element={<Layout><CarRentalBookingSuccessPage /></Layout>} />
      <Route path="/aktar" element={<RedirectAktarToCategory />} />
      <Route path="/taxi-limousine" element={<Layout><TaxiLimousinePage /></Layout>} />
      <Route path="/arac-limousine-kiralanma" element={<Navigate to="/tr/arac-limousine-kiralanma" replace />} />
      <Route path="/taxi-limousine/booking/:bookingId" element={<TaxiBookingDetail />} />
      <Route path="/hotel-flights" element={<Layout><HotelFlightsPage /></Layout>} />
      <Route path="/flights" element={<Layout><FlightSearchPage /></Layout>} />
      <Route path="/flights/booking" element={<Layout><FlightBookingPage /></Layout>} />
      <Route path="/flights/booking/success" element={<Layout><FlightBookingSuccessPage /></Layout>} />
      <Route path="/hotels" element={<Layout><HotelSearchPage /></Layout>} />
      <Route path="/hotels/:hotelId" element={<Layout><HotelDetailPage /></Layout>} />
      <Route path="/hotels/booking" element={<Layout><HotelBookingPage /></Layout>} />
      <Route path="/hotels/booking/success" element={<Layout><HotelBookingSuccessPage /></Layout>} />
      <Route path="/restaurants" element={<Layout><RestaurantsPage /></Layout>} />
      <Route path="/restaurants/:id" element={<Layout><RestaurantDetailPage /></Layout>} />
      <Route path="/supermarkets" element={<Layout><SupermarketsPage /></Layout>} />
      <Route path="/supermarkets/store/:sellerId" element={<Layout><SupermarketStorePage /></Layout>} />
      <Route path="/supermarkets/order/:orderId" element={<Layout><SupermarketOrderPage /></Layout>} />
      <Route path="/membership" element={<Layout><MembershipPage /></Layout>} />
      <Route path="/membership/success" element={<Layout><MembershipPage /></Layout>} />
      <Route path="/seller-guide" element={<Layout><SellerGuidePage /></Layout>} />
      <Route path="/search" element={<Layout><SearchPage /></Layout>} />
      <Route path="/ai" element={<Layout><AiModePage /></Layout>} />
      <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
      <Route path="/carriers" element={<Navigate to="/careers" replace />} />
      <Route path="/careers" element={<Layout><CareersPage /></Layout>} />
      <Route path="/tire-appointment" element={<Layout><TireAppointmentPage /></Layout>} />
      <Route path="/my-appointments" element={<Layout><MyAppointmentsPage /></Layout>} />
      <Route path="/about" element={<Layout><StaticPage /></Layout>} />
      <Route path="/kvkk" element={<Layout><StaticPage /></Layout>} />
      <Route path="/privacy" element={<Layout><PrivacyPolicyPage /></Layout>} />
      <Route path="/privacy-policy" element={<Layout><PrivacyPolicyPage /></Layout>} />
      <Route path="/data-protection" element={<Layout><PrivacyPolicyPage /></Layout>} />
      <Route path="/datenschutz" element={<Layout><PrivacyPolicyPage /></Layout>} />
      <Route path="/terms" element={<Layout><TermsOfServicePage /></Layout>} />
      <Route path="/terms-of-service" element={<Layout><TermsOfServicePage /></Layout>} />
      <Route path="/nutzungsbedingungen" element={<Layout><TermsOfServicePage /></Layout>} />
      <Route path="/cookies" element={<Layout><CookiePolicyPage /></Layout>} />
      <Route path="/cookie-policy" element={<Layout><CookiePolicyPage /></Layout>} />
      <Route path="/cerez-politikasi" element={<Layout><CookiePolicyPage /></Layout>} />
      <Route path="/refund-policy" element={<Layout><StaticPage /></Layout>} />
      <Route path="/shipping" element={<Layout><ShippingPage /></Layout>} />
      <Route path="/returns" element={<Layout><ReturnsPage /></Layout>} />
      <Route path="/track" element={<Layout><OrderTrackingPage /></Layout>} />
      <Route path="/page/:slug" element={<Layout><StaticPage /></Layout>} />

      {/* Language-prefixed Auth routes */}
      <Route
        path="/:lang/login"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/register"
        element={
          <LanguageWrapper>
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/forgot-password"
        element={<LanguageWrapper><ForgotPasswordPage /></LanguageWrapper>}
      />
      <Route
        path="/:lang/reset-password"
        element={<LanguageWrapper><ResetPasswordPage /></LanguageWrapper>}
      />
      <Route
        path="/:lang/verify-email"
        element={<LanguageWrapper><VerifyEmailPage /></LanguageWrapper>}
      />
      <Route
        path="/:lang/newsletter/unsubscribe"
        element={<LanguageWrapper><NewsletterUnsubscribePage /></LanguageWrapper>}
      />
      <Route
        path="/:lang/registration-success"
        element={<LanguageWrapper><RegistrationSuccessPage /></LanguageWrapper>}
      />
      <Route
        path="/:lang/resend-verification"
        element={<LanguageWrapper><RegistrationSuccessPage /></LanguageWrapper>}
      />

      {/* Legacy non-prefixed Auth routes (for backwards compatibility) */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={<ForgotPasswordPage />}
      />
      <Route
        path="/reset-password"
        element={<ResetPasswordPage />}
      />
      <Route
        path="/verify-email"
        element={<VerifyEmailPage />}
      />
      <Route
        path="/newsletter/unsubscribe"
        element={<NewsletterUnsubscribePage />}
      />
      <Route
        path="/registration-success"
        element={<RegistrationSuccessPage />}
      />
      <Route
        path="/resend-verification"
        element={<RegistrationSuccessPage />}
      />
      
      {/* Language-prefixed Seller Onboarding Routes */}
      <Route
        path="/:lang/become-seller"
        element={<LanguageWrapper><SellerOnboardingPage /></LanguageWrapper>}
      />
      <Route
        path="/:lang/seller-register"
        element={<LanguageWrapper><SellerRegisterPage /></LanguageWrapper>}
      />
      <Route
        path="/:lang/seller-registration-pending"
        element={<LanguageWrapper><SellerRegistrationPendingPage /></LanguageWrapper>}
      />
      
      {/* Legacy non-prefixed seller routes (for backwards compatibility) */}
      <Route
        path="/become-seller"
        element={<SellerOnboardingPage />}
      />
      <Route
        path="/seller-register"
        element={<SellerRegisterPage />}
      />
      <Route
        path="/seller-registration-pending"
        element={<SellerRegistrationPendingPage />}
      />

      {/* Checkout routes - Not protected for guest checkout (locale-agnostic paths) */}
      <Route path="/:lang/checkout/ziraat/fail" element={<Navigate to="/checkout/ziraat/fail" replace />} />
      <Route path="/:lang/checkout/ziraat/success" element={<Navigate to="/checkout/ziraat/success" replace />} />
      <Route path="/:lang/checkout/cancel" element={<Navigate to="/checkout/cancel" replace />} />
      <Route path="/:lang/checkout/success" element={<Navigate to="/checkout/success" replace />} />
      <Route path="/:lang/checkout" element={<Navigate to="/checkout" replace />} />
      <Route
        path="/checkout"
        element={<Layout><CheckoutPage /></Layout>}
      />
      <Route
        path="/checkout/success"
        element={<Layout><CheckoutPage /></Layout>}
      />
      <Route
        path="/checkout/cancel"
        element={<Layout><CheckoutPage /></Layout>}
      />
      <Route
        path="/checkout/ziraat/success"
        element={<Layout><ZiraatCallbackPage /></Layout>}
      />
      <Route
        path="/checkout/ziraat/fail"
        element={<Layout><ZiraatCallbackPage /></Layout>}
      />
      <Route
        path="/orders"
        element={
          <ProtectedRoute>
            <Layout><OrdersPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/orders/:id"
        element={
          <ProtectedRoute>
            <Layout><OrdersPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout><DashboardPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/settings"
        element={
          <ProtectedRoute>
            <Layout><AccountSettingsPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/account/orders"
        element={<Navigate to="/orders" replace />}
      />
      
      {/* Wishlist routes */}
      <Route
        path="/account/wishlist"
        element={
          <ProtectedRoute>
            <Layout><WishlistPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/:lang/account/wishlist"
        element={
          <LanguageWrapper>
            <ProtectedRoute>
              <Layout><WishlistPage /></Layout>
            </ProtectedRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/account/saved-restaurants"
        element={
          <ProtectedRoute>
            <Layout><SavedRestaurantsPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/:lang/account/saved-restaurants"
        element={
          <LanguageWrapper>
            <ProtectedRoute>
              <Layout><SavedRestaurantsPage /></Layout>
            </ProtectedRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/account/settings"
        element={
          <LanguageWrapper>
            <ProtectedRoute>
              <Layout><AccountSettingsPage /></Layout>
            </ProtectedRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/account/orders"
        element={<Navigate to="/orders" replace />}
      />
      <Route
        path="/:lang/dashboard"
        element={
          <LanguageWrapper>
            <ProtectedRoute>
              <Layout><DashboardPage /></Layout>
            </ProtectedRoute>
          </LanguageWrapper>
        }
      />

      {/* Seller routes with language prefix */}
      <Route
        path="/:lang/seller"
        element={
          <LanguageWrapper>
            <ProtectedRoute requireRole="seller">
              <Layout><SellerDashboardPage /></Layout>
            </ProtectedRoute>
          </LanguageWrapper>
        }
      />
      <Route
        path="/:lang/seller/bulk-upload"
        element={
          <LanguageWrapper>
            <ProtectedRoute requireRole="seller">
              <Layout><SellerBulkUploadPage /></Layout>
            </ProtectedRoute>
          </LanguageWrapper>
        }
      />
      
      {/* Seller routes without language prefix (legacy) */}
      <Route
        path="/seller"
        element={
          <ProtectedRoute requireRole="seller">
            <Layout><SellerDashboardPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/bulk-upload"
        element={
          <ProtectedRoute requireRole="seller">
            <Layout><SellerBulkUploadPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/seller/xml-mapping"
        element={
          <ProtectedRoute requireRole="seller">
            <Layout><XmlMappingPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/:lang/seller/xml-mapping"
        element={
          <ProtectedRoute requireRole="seller">
            <Layout><XmlMappingPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireRole="admin">
            <Layout><AdminDashboardPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/finance"
        element={
          <ProtectedRoute requireRole="admin">
            <Layout><FinanceDashboardPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/homepage"
        element={
          <ProtectedRoute requireRole="admin">
            <Layout><HomepageManagementPage /></Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/restaurant-emails"
        element={
          <ProtectedRoute requireRole="admin">
            <Layout><RestaurantEmailCampaign /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Language-prefixed Admin Seller Uploads */}
      <Route
        path="/:lang/admin/seller-uploads"
        element={
          <LanguageWrapper>
            <ProtectedRoute requireRole="admin">
              <Layout><SellerUploadsPage /></Layout>
            </ProtectedRoute>
          </LanguageWrapper>
        }
      />

      {/* Legacy non-prefixed route */}
      <Route
        path="/admin/seller-uploads"
        element={
          <ProtectedRoute requireRole="admin">
            <Layout><SellerUploadsPage /></Layout>
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <RouteSeo />
        <ScrollToTop />
        <GoogleReCaptchaProvider
          reCaptchaKey={recaptchaSiteKey}
          scriptProps={{ async: true, defer: true }}
        >
          <AuthProvider>
            <CurrencyProvider>
              <CartProvider>
                <WishlistProvider>
                  <ImpersonateBanner />
                  <AppRoutes />
                  <CookieConsent />
                </WishlistProvider>
              </CartProvider>
            </CurrencyProvider>
          </AuthProvider>
        </GoogleReCaptchaProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
